import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, useState } from "react";
import { Navigate } from "react-router";
import { useConnexion, useSession } from "../api/hooks";

export function Connexion() {
  const session = useSession();
  const connexion = useConnexion();

  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  // Erreurs de saisie locales, distinctes de celles renvoyées par l'API :
  // vérifier qu'un champ est rempli ne demande pas d'aller-retour réseau.
  const [manques, setManques] = useState<{ email?: string; motDePasse?: string }>({});

  // Déjà connecté : inutile de redemander, on file au tableau de bord.
  if (session.data) return <Navigate to="/" replace />;

  function soumettre(evenement: FormEvent<HTMLFormElement>) {
    evenement.preventDefault();

    const nouveauxManques: { email?: string; motDePasse?: string } = {};
    if (email.trim() === "") nouveauxManques.email = "Renseignez votre email";
    if (motDePasse === "") nouveauxManques.motDePasse = "Renseignez votre mot de passe";
    setManques(nouveauxManques);

    if (Object.keys(nouveauxManques).length > 0) return;

    connexion.mutate({ email: email.trim(), motDePasse });
  }

  const erreur = connexion.error;

  // L'API répond « Identifiants invalides » sans jamais dire lequel des deux
  // est faux : le formulaire recopie ce message tel quel. Y ajouter la moindre
  // précision (« cet email est inconnu ») transformerait l'écran en oracle
  // d'existence de comptes, exactement ce que l'API refuse de faire.
  const messageErreur = erreur?.messageGlobal;

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: "primary.main",
      }}
    >
      <Paper elevation={6} sx={{ p: { xs: 3, sm: 5 }, width: "100%", maxWidth: 440 }}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h2" component="h1">
            Delsa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Espace d'administration réservé à l'équipe.
          </Typography>
        </Stack>

        <form onSubmit={soumettre} noValidate>
          <Stack spacing={2.5}>
            {messageErreur && (
              // `role="alert"` : le message est annoncé dès son apparition,
              // sans que l'utilisateur ait à repartir le chercher.
              <Alert severity="error" role="alert">
                {messageErreur}
              </Alert>
            )}

            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(evenement) => setEmail(evenement.target.value)}
              error={Boolean(manques.email ?? erreur?.pourChamp("email"))}
              helperText={manques.email ?? erreur?.pourChamp("email")}
              autoComplete="username"
              autoFocus
              fullWidth
              required
            />

            <TextField
              label="Mot de passe"
              type="password"
              value={motDePasse}
              onChange={(evenement) => setMotDePasse(evenement.target.value)}
              error={Boolean(manques.motDePasse ?? erreur?.pourChamp("motDePasse"))}
              helperText={manques.motDePasse ?? erreur?.pourChamp("motDePasse")}
              autoComplete="current-password"
              fullWidth
              required
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={connexion.isPending}
              startIcon={
                connexion.isPending ? <CircularProgress size={18} color="inherit" /> : undefined
              }
            >
              {connexion.isPending ? "Connexion…" : "Se connecter"}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
